"use client"

import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react"
import { MarketTick } from "@/providers/ws-provider"
import {
  buildInitialDemoTicks,
  fluctuateDemoTicks,
  DEMO_SCRIP_TOKEN_MAP,
  DEMO_WATCHLIST,
  DEMO_SCRIPS,
  DEMO_PENDING_ORDERS,
  DEMO_HISTORY_ORDERS,
  DEMO_POSITIONS,
  DEMO_PROFILE,
  DEMO_FUNDS,
  DEMO_TRANSACTIONS,
  DEMO_NOTIFICATIONS,
  DEMO_PROFILE_DETAILS,
  DEMO_CLOSED_POSITIONS,
} from "@/lib/demo-data"

const TICK_INTERVAL_MS = 1500
export const DEMO_SESSION_KEY = "demo_mode"

interface DemoContextType {
  isDemoMode: boolean
  demoTicks: Record<number, MarketTick>
  demoTickHash: number
  demoScripTokenMap: Record<string, number>
  demoWatchlist: string[]
  demoScrips: typeof DEMO_SCRIPS
  demoPendingOrders: typeof DEMO_PENDING_ORDERS
  demoHistoryOrders: typeof DEMO_HISTORY_ORDERS
  demoPositions: typeof DEMO_POSITIONS
  demoClosedPositions: typeof DEMO_CLOSED_POSITIONS
  demoProfile: typeof DEMO_PROFILE
  demoFunds: typeof DEMO_FUNDS
  demoTransactions: typeof DEMO_TRANSACTIONS
  demoNotifications: typeof DEMO_NOTIFICATIONS
  demoProfileDetails: typeof DEMO_PROFILE_DETAILS
  enterDemoMode: () => void
  exitDemoMode: () => void
}

const DemoContext = createContext<DemoContextType | undefined>(undefined)

export function DemoProvider({ children }: { children: React.ReactNode }) {
  const [isDemoMode, setIsDemoMode] = useState(false)
  const [demoTicks, setDemoTicks] = useState<Record<number, MarketTick>>({})
  const [demoTickHash, setDemoTickHash] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Restore demo mode from sessionStorage on mount (handles page refreshes)
  useEffect(() => {
    if (typeof window !== "undefined" && sessionStorage.getItem(DEMO_SESSION_KEY) === "true") {
      setIsDemoMode(true)
      setDemoTicks(buildInitialDemoTicks())
    }
  }, [])

  // Start / stop tick simulation when demo mode changes
  useEffect(() => {
    if (!isDemoMode) {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
      return
    }
    intervalRef.current = setInterval(() => {
      setDemoTicks((prev) => {
        const next = fluctuateDemoTicks(prev)
        setDemoTickHash((h) => h + 1)
        return next
      })
    }, TICK_INTERVAL_MS)
    return () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null }
    }
  }, [isDemoMode])

  const enterDemoMode = useCallback(() => {
    sessionStorage.setItem(DEMO_SESSION_KEY, "true")
    setDemoTicks(buildInitialDemoTicks())
    setIsDemoMode(true)
  }, [])

  const exitDemoMode = useCallback(() => {
    sessionStorage.removeItem(DEMO_SESSION_KEY)
    // ?welcome=1 opts out of the pre-paint root guard in app/layout.tsx so
    // exiting the demo lands on the welcome screen, not on the login page the
    // guard would pick for an already-onboarded device.
    window.location.href = "/?welcome=1"
  }, [])

  return (
    <DemoContext.Provider
      value={{
        isDemoMode,
        demoTicks,
        demoTickHash,
        demoScripTokenMap: DEMO_SCRIP_TOKEN_MAP,
        demoWatchlist: DEMO_WATCHLIST,
        demoScrips: DEMO_SCRIPS,
        demoPendingOrders: DEMO_PENDING_ORDERS,
        demoHistoryOrders: DEMO_HISTORY_ORDERS,
        demoPositions: DEMO_POSITIONS,
        demoClosedPositions: DEMO_CLOSED_POSITIONS,
        demoProfile: DEMO_PROFILE,
        demoFunds: DEMO_FUNDS,
        demoTransactions: DEMO_TRANSACTIONS,
        demoNotifications: DEMO_NOTIFICATIONS,
        demoProfileDetails: DEMO_PROFILE_DETAILS,
        enterDemoMode,
        exitDemoMode,
      }}
    >
      {children}
    </DemoContext.Provider>
  )
}

export function useDemoMode() {
  const ctx = useContext(DemoContext)
  if (!ctx) throw new Error("useDemoMode must be used inside DemoProvider")
  return ctx
}
